import CoachInfo from "@/Components/ClientCoachInfo/CoachInfo/CoachInfo";
import PrimaryButton from "@/Components/UI/Forms/PrimaryButton";
import Modal from "@/Components/UI/Modal";
import ConfirmResumeSubscriptionModal from "@/Components/UI/Modals/ConfirmResumeSubscriptionModal";
import PaymentForm, {
    PaymentFormData,
} from "@/Components/UI/Onboarding/Client/PaymentForm/PaymentForm";
import { AnalyticsContext } from "@/Contexts/AnalyticsContext";
import { PayUsModalsContext } from "@/Contexts/PayUsModalsContext";
import ProactivePaymentFaq from "@/Pages/Chat/Partials/ProactivePaymentFaq";
import { Client, ClientCoach, Coach, PageProps } from "@/types/page";
import dataLayerUserData from "@/utils/dataLayer";
import { coachWithCerts } from "@/utils/fullUserNames";
import { usePage } from "@/utils/turboSafeInertia";
import { isBefore } from "date-fns";
import { PropsWithChildren, useContext, useEffect, useState } from "react";
import { RouteName } from "ziggy-js";

type PayUsModalData = {
    clientCoach: ClientCoach & {
        client: Client;
        coach: Coach;
    };
    hasSubscription: boolean;
    subscriptionEndsAt: string | null;
    isPostPaymentCoachPickTreatment: boolean;
};

const INITIALLY_SHOW_ON_ROUTES: RouteName[] = [
    "chat",
    "client.my-sessions",
    "client.profile.edit",
];

function PayUsModalFaq({
    paymentFormData,
}: {
    paymentFormData: PaymentFormData;
}) {
    return (
        <ProactivePaymentFaq
            paymentFormData={paymentFormData}
            includeCoachingEvidence
        />
    );
}

export const PayUsModalsProvider = ({ children }: PropsWithChildren) => {
    const { user } = usePage<PageProps>().props;

    const { trackEvent } = useContext(AnalyticsContext);

    // if the query parameter pay=1 is set, show the payment modal on load
    // instead of the matched modal
    const queryParams = new URLSearchParams(window.location.search);
    const showPaymentModalFromQueryString = queryParams.has("pay", "1");
    const currentRoute = route().current();
    const shouldInitiallyShow =
        !!currentRoute &&
        INITIALLY_SHOW_ON_ROUTES.includes(currentRoute) &&
        user?.type === "client";

    const [showMatchedModal, setShowMatchedModal] = useState(false);

    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [payUsModalData, setPayUsModalData] = useState<PayUsModalData>();

    const markMatchAsSeen = () => {
        if (
            payUsModalData &&
            !payUsModalData.clientCoach.has_client_seen_match
        ) {
            window.axios.patch(
                route("hasClientSeenMatch", {
                    coach: payUsModalData.clientCoach.coach,
                }),
            );
        }
    };

    const fetchPayUsModalData = async () => {
        const response = await window.axios.get(route("getPayUsModalData"));
        const payUsModalData = response.data as PayUsModalData;
        setPayUsModalData(payUsModalData);
        return payUsModalData;
    };

    const showPaymentModalsIfNecessary = async (eventDate?: string) => {
        if (user?.type !== "client") return false;
        const { clientCoach, hasSubscription, subscriptionEndsAt } =
            await fetchPayUsModalData();
        const noEventDateAndInactiveOrWillBeInactive =
            eventDate === undefined &&
            (!clientCoach.is_active || !!subscriptionEndsAt);
        const eventDateAfterSubscriptionEnds =
            eventDate !== undefined &&
            isBefore(subscriptionEndsAt ?? Infinity, eventDate);
        const clientHasNeverSubscribed = !hasSubscription;

        const isPaymentModalNecessary =
            user?.type === "client" &&
            (clientHasNeverSubscribed ||
                noEventDateAndInactiveOrWillBeInactive ||
                eventDateAfterSubscriptionEnds);

        if (isPaymentModalNecessary) {
            setShowPaymentModal(true);
        }
        return isPaymentModalNecessary;
    };

    const handleInitialShowing = async () => {
        const paymentModalData = await fetchPayUsModalData();
        if (!paymentModalData.clientCoach.has_client_seen_match) {
            setShowMatchedModal(true);
        } else if (showPaymentModalFromQueryString) {
            showPaymentModalsIfNecessary();
        }
    };

    useEffect(() => {
        if (showPaymentModal && user?.type === "client") {
            dataLayer.push({
                event: "initiate_checkout",
                ...dataLayerUserData(user),
            });
            trackEvent("initiate_checkout", {
                current_page: currentRoute ?? "unknown",
                checkout_source: "pay_us_modal_context",
                pricing_version_id: user.pricing_version_id ?? "unknown",
            });
        }
    }, [showPaymentModal, currentRoute]);

    useEffect(() => {
        if (!shouldInitiallyShow) return;
        handleInitialShowing();
    }, [shouldInitiallyShow, user?.type]);

    return user?.type === "client" ? (
        <PayUsModalsContext.Provider
            value={{
                showPaymentModalsIfNecessary,
            }}
        >
            <Modal
                testId="youve-been-paired-with-modal"
                id="youve-been-paired-with"
                show={showMatchedModal}
                prepareToShow={shouldInitiallyShow && !payUsModalData}
                onClose={() => {
                    setShowMatchedModal(false);
                    markMatchAsSeen();
                }}
                showCloseButton={true}
            >
                {payUsModalData && (
                    <>
                        <div
                            data-testid="modal-title"
                            className="mt-8 px-6 pt-2 text-center text-xl font-medium sm:px-10 sm:text-start"
                        >
                            {payUsModalData.isPostPaymentCoachPickTreatment ? (
                                <>
                                    Start your coaching journey with{" "}
                                    {
                                        payUsModalData.clientCoach.coach
                                            .first_name
                                    }{" "}
                                    {payUsModalData.clientCoach.coach.last_name}
                                </>
                            ) : (
                                <>
                                    You've been paired with{" "}
                                    {coachWithCerts(
                                        payUsModalData.clientCoach.coach,
                                    )}
                                </>
                            )}
                        </div>
                        <div className="px-0 pb-0 sm:pb-4 sm:px-4">
                            <CoachInfo
                                coachId={payUsModalData.clientCoach.coach.id}
                                config={{
                                    topItemsAndOptions: ["license", "ratings"],
                                }}
                            />
                            <div className="sticky bottom-0 bg-brand-white p-3">
                                <PrimaryButton
                                    data-testid="start-coaching-button"
                                    className="w-full justify-center py-3"
                                    onClick={() => {
                                        setShowMatchedModal(false);
                                        showPaymentModalsIfNecessary();
                                        markMatchAsSeen();
                                    }}
                                >
                                    {payUsModalData.isPostPaymentCoachPickTreatment
                                        ? "Continue"
                                        : `Start coaching with ${payUsModalData.clientCoach.coach.first_name}`}
                                </PrimaryButton>
                            </div>
                        </div>
                    </>
                )}
            </Modal>
            <Modal
                id="pay-us-modal"
                show={
                    showPaymentModal &&
                    !!payUsModalData &&
                    !payUsModalData.hasSubscription
                }
                onClose={() => setShowPaymentModal(false)}
                showCloseButton={true}
                disableBackgroundClickToClose={true}
            >
                {/* The payment form has padding inside and it's gross, but it's easier to just override it with negative margin here */}
                <div className="-my-8">
                    <PaymentForm
                        onPaymentComplete={() => setShowPaymentModal(false)}
                        BetweenPricingAndPaymentComponent={PayUsModalFaq}
                    />
                </div>
            </Modal>
            <ConfirmResumeSubscriptionModal
                isOpen={
                    showPaymentModal &&
                    !!payUsModalData &&
                    payUsModalData.hasSubscription
                }
                onClose={() => setShowPaymentModal(false)}
            />
            {children}
        </PayUsModalsContext.Provider>
    ) : (
        children
    );
};
